import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatClock } from '@racedash/shared'
import { useSearchParams } from 'react-router-dom'
import { Alert, Badge, Button, Group, Select, Tabs, Text } from '@mantine/core'
import {
  UnsavedChangesGuard,
  byCarNumber,
  isKnownScreen,
  neededDataForScreens,
  parseScreens,
  screensEqual,
} from '@racedash/shared'
import type { DataNeed, ScreenEntry } from '@racedash/shared'
import { useLocalSetting } from '../lib/useLocalSetting'
import { saveDefaultCar, saveScreens } from '../dashboards/api'
import ScreenView from './ScreenView'
import ScreenSwitcher from './ScreenSwitcher'
import ScreenManager from './edit/ScreenManager'
import SourceBar, { eventStatus } from './SourceBar'
import SeriesResolver from './SeriesResolver'
import ConfirmStop from './ConfirmStop'
import { sampleCar, sampleData, sampleScenarios } from './sampleData'
import type { SampleScenario } from './sampleData'
import { useDashboardSource, type Selection } from './useDashboardSource'
import { useDeclarations } from './useDeclarations'
import { stopKey, useFuelMarks } from './useFuelBurn'
import type { StopFuelAnswers } from './useFuelBurn'
import type { ScreenContext } from './screenContext'
import { useDrivers } from '../team/api'
import { useActivePlan } from '../racePlan/api'
import { useNow, useSessionData } from './useSessionData'
import ReplayBar from './ReplayBar'
import { replayData, useReplayClock, useReplayFrame, useReplaySource } from './replay'
import { ReplayContext, type Replay } from './replayContext'

/**
 * A dashboard: several screens, over whatever this team is racing.
 *
 * It names no event. A layout is not about a weekend, and binding one to an
 * event meant either a dashboard per event or editing the same dashboard every
 * fortnight — neither of which is what a dashboard is for.
 *
 * What data it may show comes from the organisation's own entries, which it
 * already has to declare to be captured at all. What it IS showing lives in the
 * URL: that keeps it pasteable, and keeps one person changing session from
 * yanking a colleague's screen mid-race.
 */
export default function LiveDashboard({
  dashboardId,
  orgId,
  stored,
  canEdit,
}: {
  dashboardId: string
  orgId: string
  stored: { screens?: unknown[]; layout?: unknown; defaultCar?: string }
  canEdit: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [scenario, setScenario] = useState<SampleScenario>('midRace')
  const [saveError, setSaveError] = useState<string | null>(null)
  /*
   * Filling the browser window, as against handing the display to the browser.
   *
   * Remembered per dashboard, because the reason to turn it on is usually that
   * this dashboard lives on a screen somebody walks past — and having to set
   * that again after every reload would defeat it. Escape gets out, and the
   * button stays visible, so it is not a state anybody can be trapped in.
   */
  const [filling, setFilling] = useLocalSetting<boolean>(
    `racedash.dash.${dashboardId}.filling`,
    false,
    (raw) => raw === true,
  )

  const [saving, setSaving] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const now = useNow()
  const [params, setParams] = useSearchParams()

  // --- what is stored -----------------------------------------------------

  const baseline: ScreenEntry[] = useMemo(
    () => parseScreens(stored.screens, stored.layout),
    [stored.screens, stored.layout],
  )
  const [draft, setDraft] = useState(baseline)

  /**
   * Adopt what the server says, but only while there is nothing of our own to
   * lose. This also settles the echo after a save: the write comes back through
   * the listener, the draft already equals it, and the adoption is a no-op.
   */
  const adopted = useRef(baseline)
  useEffect(() => {
    const previous = adopted.current
    adopted.current = baseline
    setDraft((current) => (screensEqual(current, previous) ? baseline : current))
  }, [baseline])

  const dirty = !screensEqual(draft, baseline)
  const screens = editing ? draft : baseline
  const active = screens.find((s) => s.id === activeId) ?? screens[0] ?? null

  // --- where the data comes from ------------------------------------------

  /**
   * The selection lives in the query string.
   *
   * Not on the dashboard document, which is shared: two people on one team can
   * be looking at different sessions, and storing it server-side would mean
   * either of them could yank the other's view. Not component state either, or
   * "look at this" stops being a link.
   */
  const selection: Selection = useMemo(
    () => ({
      eventId: params.get('event'),
      sessionId: params.get('session'),
      car: params.get('car'),
    }),
    [params],
  )

  const select = useCallback(
    (next: Selection) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          for (const [key, value] of [
            ['event', next.eventId],
            ['session', next.sessionId],
            ['car', next.car],
          ] as const) {
            if (value) out.set(key, value)
            else out.delete(key)
          }
          return out
        },
        // Replace rather than push: changing session is adjusting a view, not
        // navigating, and a back button that walks through every session
        // somebody skimmed is worse than useless during a race.
        { replace: true },
      )
    },
    [setParams],
  )

  /**
   * The car the widgets mean, and the tie-break between two live sessions.
   *
   * The URL wins, because that is this viewing; the dashboard's pin is the
   * fallback, because that is what the dashboard is for. Without the fallback a
   * freshly opened dashboard has no preferred car at all, and the tie-break
   * that makes a team in two places work would never fire.
   */
  const car = selection.car ?? stored.defaultCar ?? null
  const source = useDashboardSource(orgId, { ...selection, car }, now)
  const sessionId = source.session?.id ?? null

  /*
   * Replaying the session: everything on screen as it stood at an instant the
   * viewer moves about. In the URL like the session, so a reload stays in the
   * replay — and so a link can say "look at this race again".
   */
  const replaying = params.get('replay') === '1' && sessionId != null
  const setReplaying = useCallback(
    (on: boolean) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          if (on) out.set('replay', '1')
          else out.delete('replay')
          return out
        },
        { replace: true },
      ),
    [setParams],
  )
  const replaySource = useReplaySource(replaying ? sessionId : null)
  const replayClock = useReplayClock(replaying ? sessionId : null, {
    start: replaySource.index?.start ?? null,
    end: replaySource.index?.end ?? null,
  })
  const frame = useReplayFrame(replaying ? replaySource.index : null, replayClock.at)
  const replay: Replay | null = useMemo(
    () => (replaying && frame && sessionId ? { sessionId, at: frame.at, frame } : null),
    [replaying, frame, sessionId],
  )
  // The instant the screens are shown: the replayed one, or now.
  const shownNow = replay?.at ?? now

  /**
   * The organisation's own layer, threaded to every screen.
   *
   * Separate from the timing, which is a fact about a public race feed and
   * identical for everybody. This is whose dashboard it is: which cars are
   * theirs, who they say is driving, and whether this viewer may say otherwise.
   */
  const { items: drivers } = useDrivers(orgId)
  const declarations = useDeclarations(sessionId, orgId)
  // Whichever of the team's race plans covers what is on screen, resolved
  // against the plans' own start and end times rather than a catalogue session
  // — a timetable is a convenience, and most events do not have one.
  const plan = useActivePlan(orgId, source.event?.eventId, shownNow)
  const fuelMarks = useFuelMarks(source.event?.eventId ?? null, orgId)
  const [replayStops, setReplayStops] = useState<StopFuelAnswers>({})
  const [replayStopsOf, setReplayStopsOf] = useState<string | null>(null)
  if (replayStopsOf !== (replaying ? sessionId : null)) {
    setReplayStopsOf(replaying ? sessionId : null)
    setReplayStops({})
  }
  const answerReplayStop = useCallback(async (session: string, car: string, stopNumber: number, fuelled: boolean) => {
    setReplayStops((was) => ({ ...was, [session]: { ...was[session], [stopKey(car, stopNumber)]: fuelled } }))
  }, [])
  // The team's sessions at the event, for what a tank lasted across them.
  const fuelSessions = useMemo(
    () =>
      source.ours.flatMap((s) => {
        const start = s.clockStartAt?.toDate() ?? s.firstSeenAt?.toDate()
        return start ? [{ id: s.id, start, cars: s.cars ?? [] }] : []
      }),
    [source.ours],
  )
  // One derivation, shared by the screens and by the car picker, so "ours"
  // cannot mean two different things on one page.
  const ourCars = useMemo(
    () => (source.event?.entries ?? []).map((e) => e.number).filter(Boolean),
    [source.event],
  )

  const context: ScreenContext = useMemo(
    () => ({
      viewId: dashboardId,
      ourCars,
      drivers: (drivers ?? []).map((d) => ({ id: d.id, name: d.name, shortName: d.shortName ?? null })),
      entryDrivers: Object.fromEntries(
        (source.event?.entries ?? [])
          .filter((e) => e.number)
          .map((e) => [e.number, e.driverIds ?? []]),
      ),
      declarations: declarations.changes,
      // Only for someone who may edit. Screens test for the function rather
      // than being told separately whether editing is allowed, so an editable
      // control cannot be rendered with nothing behind it.
      // Nor during a replay, which is looking at the past rather than saying
      // anything about it.
      declare: canEdit && !replaying ? declarations.declare : undefined,
      plan,
      sessionStart: plan?.startsAt != null ? new Date(plan.startsAt) : null,
      sessionEnd: plan?.endsAt != null ? new Date(plan.endsAt) : null,
      fuel: source.event
        ? {
            eventId: source.event.eventId,
            sessions: fuelSessions,
            marks: fuelMarks.marks,
            // Whoever may say who is in the car may say how full it was.
            mark: canEdit && !replaying ? fuelMarks.mark : undefined,
            // During a replay, said for the replay only and forgotten after:
            // trying out the question without writing over the race's answers.
            stops: replaying ? replayStops : fuelMarks.stops,
            answerStop: replaying ? answerReplayStop : canEdit ? fuelMarks.answerStop : undefined,
          }
        : undefined,
    }),
    [
      dashboardId,
      ourCars,
      source.event,
      drivers,
      declarations,
      canEdit,
      replaying,
      plan,
      fuelSessions,
      fuelMarks,
      replayStops,
      answerReplayStop,
    ],
  )

  /*
   * What the screens need, plus what the dashboard itself needs.
   *
   * The stop-confirmation prompt is not a widget. It sits over whichever screen
   * is showing, and it reads pit stops, standings, laps and stints — and it used
   * to get them only if some tile on the grid happened to ask. Remove the lap
   * list and the prompt quietly stopped firing; it worked by coincidence. The
   * dashboard's own needs are stated here, and only where the prompt can
   * appear at all, which is for somebody who may declare.
   */
  const needs = useMemo(() => {
    const own: DataNeed[] = canEdit ? ['pitStops', 'standings', 'laps', 'stints'] : []
    return [...new Set<DataNeed>([...neededDataForScreens(screens), ...own])]
  }, [screens, canEdit])
  // Not listened to during a replay, which has read everything it needs.
  const liveData = useSessionData(replaying ? null : sessionId, car, needs)
  const replayed = useMemo(
    () => (replay ? replayData(replay.frame, replay.sessionId, car, liveData.clock) : null),
    [replay, car, liveData.clock],
  )

  /**
   * Sample data while arranging with nothing to arrange against.
   *
   * Only while editing, and only while no session has resolved, so it can never
   * be mistaken for live timing on a dashboard someone is reading.
   */
  const usingSample = editing && !source.session
  const sample = useMemo(() => sampleData(scenario), [scenario])
  const data = replayed ?? (usingSample ? sample : liveData)

  /*
   * The car picker's contents.
   *
   * By car number, not by position. Position was the old order and it is the
   * wrong one: this is a list you look a number up in, and a list that
   * reorders itself every lap is one you cannot look anything up in. Numbers
   * are read as numbers where they are, so 7 comes before 58 comes before 436.
   *
   * The team's own cars are lifted into a group of their own at the top. On a
   * sixty-car grid the car you came to watch should not have to be hunted for,
   * and Mantine renders the group heading, so "ours" is stated rather than
   * implied by position in the list.
   */
  const cars = useMemo(() => {
    const list = (data.standings?.cars ?? []).filter((c) => c.number).slice()
    list.sort((a, b) => byCarNumber(a.number, b.number))
    const option = (c: (typeof list)[number]) => ({
      value: c.number,
      label: c.class ? `${c.number} — ${c.class}` : c.number,
    })

    // Ours from the entries, not from the standings. The team said which cars
    // it is running before anything was on track, and a car picker that
    // waited for the first standings made it impossible to set a dashboard up
    // ahead of the session — which is when it gets set up. The standings only
    // add the class once there are some.
    const mine = new Set(ourCars)
    const byNumber = new Map(list.map((c) => [c.number, c]))
    const ours = [...mine]
      .sort(byCarNumber)
      .map((n) => (byNumber.has(n) ? option(byNumber.get(n)!) : { value: n, label: n }))
    const rest = list.filter((c) => !mine.has(c.number)).map(option)
    if (ours.length === 0) return rest
    if (rest.length === 0) return [{ group: 'Ours', items: ours }]
    return [
      { group: 'Ours', items: ours },
      { group: 'Everyone else', items: rest },
    ]
  }, [data.standings, ourCars])

  // Escape leaves, matching what the same key does in real full screen. Only
  // bound while filling, so it cannot swallow Escape from a dialog underneath.
  useEffect(() => {
    if (!filling) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilling(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filling, setFilling])

  const save = useCallback(async () => {
    setSaving(true)
    setSaveError(null)
    try {
      await saveScreens(dashboardId, draft)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }, [dashboardId, draft])

  return (
    <section className="dash-host" data-filling={filling || undefined}>
      <UnsavedChangesGuard
        when={dirty}
        message="This dashboard's screens have not been saved. Leaving will discard your changes."
      />

      <Group justify="space-between" align="flex-end" mb="sm" wrap="wrap">
        <SourceBar
          state={source}
          selection={{ ...selection, car }}
          onChange={select}
          cars={cars}
          defaultCar={stored.defaultCar}
          onPinCar={
            canEdit
              ? (next) => {
                  void saveDefaultCar(dashboardId, next).catch((e) =>
                    setSaveError(e instanceof Error ? e.message : String(e)),
                  )
                }
              : undefined
          }
          onResolveSeries={canEdit ? () => setResolving(true) : undefined}
          eventsHref={`/o/${orgId}/events`}
        />

        <Group gap="xs">
          {editing ? (
            <>
              <Button variant="default" disabled={!dirty} onClick={() => setDraft(baseline)}>
                Revert
              </Button>
              <Button disabled={!dirty} loading={saving} onClick={() => void save()}>
                Save
              </Button>
              <Button variant="subtle" onClick={() => setEditing(false)}>
                Done
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button variant="default" onClick={() => setEditing(true)}>
                  Edit screens
                </Button>
              )}
              {/*
                Two different things, deliberately offered separately.

                "Fill window" is the browser's own window with everything else
                out of the way — the tab strip, the address bar and the URL are
                all still there, which is what you want on a machine you are
                also using for other things, or when you want to be able to see
                where you are.

                "Full screen" hands the display to the browser: no chrome at
                all, and on most machines no obvious way back except a key. That
                is the right answer for a screen bolted to a pit wall and the
                wrong one for a laptop on a desk.
              */}
              {source.session && !replaying && (
                <Button variant="default" onClick={() => setReplaying(true)}>
                  Replay
                </Button>
              )}
              <Button variant="default" onClick={() => setFilling(!filling)}>
                {filling ? 'Exit' : 'Fill window'}
              </Button>
              <Button
                variant="default"
                onClick={() => void stageRef.current?.requestFullscreen?.()}
              >
                Full screen
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Group gap="sm" mb="xs" align="center" wrap="wrap">
        {screens.length > 0 && (
          <Tabs value={active?.id ?? null} onChange={setActiveId} variant="pills">
            <Tabs.List>
              {screens.map((s) => (
                <Tabs.Tab key={s.id} value={s.id}>
                  {/* A screen this build cannot draw still gets its tab, so it
                      is visible rather than silently missing. */}
                  {s.name}
                  {!isKnownScreen(s) && ' ⚠'}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}
        {editing && (
          <ScreenManager screens={draft} onChange={setDraft} activeId={active?.id ?? null} />
        )}
      </Group>

      {usingSample && (
        <Group gap="sm" align="center" mb="xs" wrap="wrap">
          <Badge color="grape" variant="light">
            Sample data
          </Badge>
          <Text size="xs" c="dimmed">
            Nothing here is live. Bind an event above to arrange against real timing.
          </Text>
          <Select
            size="xs"
            w={180}
            aria-label="Sample scenario"
            data={sampleScenarios.map((s) => ({ value: s.id, label: s.label }))}
            value={scenario}
            onChange={(v) => v && setScenario(v as SampleScenario)}
            allowDeselect={false}
          />
        </Group>
      )}

      {/* Naming whoever just took over, prefilled from the rotation. The stint
          count rule needs every stint attributed, and a race is a dozen stops —
          so it only works if saying who got in is close to free. */}
      {replaying && (
        <ReplayBar
          clock={replayClock}
          loading={replaySource.loading}
          error={replaySource.error}
          onExit={() => setReplaying(false)}
        />
      )}

      {!replaying && <ConfirmStop car={car} data={data} now={now} context={context} />}

      {source.error && (
        <Alert color="red" mb="sm">
          Could not read the sessions for this event: {source.error}
        </Alert>
      )}
      {saveError && (
        <Alert color="red" mb="sm" title="Not saved">
          {saveError}
        </Alert>
      )}
      {source.session && !source.freshness.live && !usingSample && !replaying && (
        // Worth saying out loud: a frozen screen and a green-flag lull look
        // identical, and only one of them means the racing is over.
        <Alert color="yellow" mb="sm">
          This session is not live — it last updated at{' '}
          {source.session.lastSeenAt ? formatClock(source.session.lastSeenAt.toDate(), { seconds: true }) : '—'}.
        </Alert>
      )}

      {screens.length === 0 ? (
        <div className="dash-frame dash-frame--idle">
          <Text c="dimmed" size="sm" ta="center">
            {canEdit
              ? 'No screens yet. Choose "Edit screens" to add one.'
              : 'This dashboard has no screens yet.'}
          </Text>
        </div>
      ) : !source.session && !editing ? (
        <div className="dash-frame dash-frame--idle">
          <Text c="dimmed" size="sm" ta="center">
            {/* Empty and broken must not read the same. An event that has not
                started yet is a countdown; an event with no entries is a setup
                step; and only silence with neither of those is a fault. */}
            {emptyReason(source, now)}
          </Text>
        </div>
      ) : (
        <ReplayContext.Provider value={replay}>
          <div className="dash-frame" ref={stageRef}>
            {/* Going full screen takes the stage and leaves the tabs behind, so
                the switcher has to live inside it. */}
            <ScreenSwitcher
              screens={screens}
              activeId={active?.id ?? null}
              onSelect={setActiveId}
              stageRef={stageRef}
            />
            {active && (
              <ScreenView
                screen={active}
                editing={editing}
                onChange={(next) => setDraft((cur) => cur.map((s) => (s.id === next.id ? next : s)))}
                data={data}
                primaryCar={usingSample && !car ? sampleCar : car}
                now={shownNow}
                context={context}
              />
            )}
          </div>
        </ReplayContext.Provider>
      )}

      {resolving && source.event && (
        <SeriesResolver
          event={source.event}
          sessions={[...source.ours, ...source.others]}
          orgId={orgId}
          onClose={() => setResolving(false)}
        />
      )}
    </section>
  )
}

/**
 * Why there is nothing on screen.
 *
 * Four different reasons, and they need four different responses — waiting,
 * setting something up, fixing a name, or investigating. Collapsing them into
 * "no data" is the same mistake as a status page that reads silence as health.
 */
function emptyReason(source: ReturnType<typeof useDashboardSource>, now: Date): string {
  if (source.loading) return 'Loading…'
  if ((source.events?.length ?? 0) === 0) {
    return 'Tell us which events you are entering, and what you are running — that is what starts capture.'
  }
  if (source.unmatched === 'waiting') {
    return 'Other series have run here, but not yours yet.'
  }
  if (source.unmatched === 'noSeries') {
    return 'Your entry here has no series set. Choose one on the Events page.'
  }
  if (source.unmatched === 'unconfigured') {
    return 'No channel is set for your series at this event yet, so nothing captured here can be shown as yours.'
  }
  if (source.unmatched) {
    return 'Sessions were captured here, but none is in a series your entries name. Use "Pick which are ours" above.'
  }
  const status = eventStatus(source.event, now)
  if (source.ours.length === 0 && source.others.length === 0) {
    return status ? `Nothing captured yet. ${status}` : 'Nothing has been captured yet.'
  }
  return 'Pick a session above.'
}
